import React, { FC, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './top-up-pot-modal.scss';

interface TopUpPotModalProps {
  isOpen: boolean;
  onClose: () => void;
  topUpOption: {
    priceId: string;
    unitAmount: number;
    unitsPerProduct: number;
    currency: string;
  } | null;
  assistantName: string;
  onSuccess: () => void;
}

function formatCurrency(amount: number, currency = 'GBP'): string {
  try {
    const isWhole = amount % 1 === 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
}

const TopUpPotModal: FC<TopUpPotModalProps> = ({
  isOpen,
  onClose,
  topUpOption,
  assistantName,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [amount, setAmount] = useState('20');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setAmount('20');
    setError(null);
    onClose();
  }, [onClose]);

  const parsedAmount = parseInt(amount, 10) || 0;
  const quantity = topUpOption
    ? Math.max(1, Math.ceil(parsedAmount / topUpOption.unitAmount))
    : 1;
  const totalCost = topUpOption ? topUpOption.unitAmount * quantity : 0;

  const handleConfirm = useCallback(async () => {
    if (!topUpOption || submitting || parsedAmount <= 0) return;
    setSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post(
        '/credits/topup-balance',
        { priceId: topUpOption.priceId, quantity },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data.success) {
        setAmount('20');
        onSuccess();
      } else {
        setError('Top-up failed. Please try again.');
      }
    } catch (err) {
      console.error('Error topping up pot:', err);
      setError('Top-up failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [
    topUpOption,
    quantity,
    parsedAmount,
    submitting,
    getAccessTokenSilently,
    onSuccess,
  ]);

  if (!isOpen || !topUpOption) return null;

  return (
    <div className='topup-pot-overlay' onClick={handleClose}>
      <div className='topup-pot-modal' onClick={(e) => e.stopPropagation()}>
        <div className='topup-pot-header'>
          <p className='topup-pot-title'>Top up {assistantName}&rsquo;s Card</p>
          <button className='topup-pot-close' onClick={handleClose}>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='topup-pot-body'>
          <p className='topup-pot-desc'>
            Add funds to {assistantName}&rsquo;s Card. Your payment card will be
            charged immediately.
          </p>

          <div className='topup-pot-amount-row'>
            <span className='topup-pot-amount-label'>Amount</span>
            <div className='topup-pot-stepper'>
              <button
                className='topup-pot-step-btn'
                onClick={() => {
                  const step = parsedAmount <= 10 ? 1 : 10;
                  setAmount(String(Math.max(0, parsedAmount - step)));
                }}
                disabled={parsedAmount <= 0}
              >
                −
              </button>
              <div className='topup-pot-input-wrap'>
                <span className='topup-pot-currency-symbol'>£</span>
                <input
                  className='topup-pot-input'
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  placeholder='0'
                  value={amount}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setAmount(v);
                  }}
                  autoFocus
                />
              </div>
              <button
                className='topup-pot-step-btn'
                onClick={() => {
                  const step = parsedAmount < 10 ? 1 : 10;
                  setAmount(String(parsedAmount + step));
                }}
              >
                +
              </button>
            </div>
          </div>

          {error && <p className='topup-pot-error'>{error}</p>}
        </div>

        <div className='topup-pot-footer'>
          <button className='topup-pot-cancel' onClick={handleClose}>
            Cancel
          </button>
          <button
            className='topup-pot-confirm'
            onClick={handleConfirm}
            disabled={submitting || parsedAmount <= 0}
          >
            {submitting
              ? 'Processing...'
              : `Top up ${parsedAmount > 0 ? formatCurrency(totalCost, topUpOption.currency) : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopUpPotModal;
